import React from "react";
import { Button, Col, DatePicker, Form, Input, PaginationProps, Row, Select, Space, Table, Tabs, Tag } from "antd";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiDownload } from "@mdi/js";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import { BookingSummary } from "../../interfaces/booking.interface";

const { Column } = Table;
const LIMIT = 25;
const itemRender: PaginationProps['itemRender'] = (_, type, originalElement) => {
  if (type === 'prev') return <a>Previous</a>;
  if (type === 'next') return <a>Next</a>;
  return originalElement;
};

const statusesColor = {
  Awaiting: "#FFBE3D",
  "Awaiting Payment": "#FFBE3D",
  "Pending Confirmation": "#FFBE3D",
  "Partial Paid": "#23D1C0",
  Cancelled: "#ACACAC",
  Withdrawn: "#ACACAC",
  Paid: "#6BDF63",
};

interface BookingHistoryState {
  total: number;
  bookings: BookingSummary[];
  currentPage: number;
  category: "series" | "ground";
  filters: Record<string, string>;
  loading: boolean;
}

class BookingHistoryPage extends React.Component {
  filterForm = React.createRef<any>();

  state: BookingHistoryState = {
    total: 0,
    bookings: [],
    currentPage: 1,
    category: "series",
    filters: {},
    loading: false,
  };

  componentDidMount(): void {
    this.getBookings();
  }

  getBookings = (page = 1, category = this.state.category, filters = this.state.filters) => {
    this.setState({ loading: true });
    const request = category === "ground"
      ? B2C2BApi.getLandTourBookings(LIMIT, page, filters)
      : B2C2BApi.getBookings(LIMIT, page);

    request
      .then(({ bookings, total }) => {
        this.setState({
          bookings: bookings.map(booking => ({ ...booking, key: `${category}-${booking.id}` })),
          total,
          currentPage: page,
        });
      })
      .finally(() => this.setState({ loading: false }));
  };

  changeCategory = (category: "series" | "ground") => {
    this.filterForm.current?.resetFields();
    this.setState({ category, filters: {}, bookings: [], total: 0, currentPage: 1 }, () => {
      this.getBookings(1, category, {});
    });
  };

  applyFilters = (values) => {
    const filters = {
      ...values,
      book_date_from: values.book_date?.[0]?.format('YYYY-MM-DD'),
      book_date_to: values.book_date?.[1]?.format('YYYY-MM-DD'),
    };
    delete filters.book_date;
    this.setState({ filters }, () => this.getBookings(1, "ground", filters));
  };

  resetFilters = () => {
    this.filterForm.current?.resetFields();
    this.setState({ filters: {} }, () => this.getBookings(1, "ground", {}));
  };

  groundFilters() {
    return (
      <Form ref={this.filterForm} layout="vertical" onFinish={this.applyFilters} className="booking-history-filters">
        <Row gutter={[12, 0]} align="bottom">
          <Col xs={24} md={8} xl={5}>
            <Form.Item name="book_date" label="Booking Date"><DatePicker.RangePicker style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col xs={24} md={8} xl={4}>
            <Form.Item name="name" label="Name"><Input allowClear /></Form.Item>
          </Col>
          <Col xs={24} md={8} xl={4}>
            <Form.Item name="ref_no" label="Ref No."><Input allowClear /></Form.Item>
          </Col>
          <Col xs={24} md={8} xl={4}>
            <Form.Item name="tour_code" label="Tour Code"><Input allowClear /></Form.Item>
          </Col>
          <Col xs={24} md={8} xl={4}>
            <Form.Item name="payment_status" label="Payment Status">
              <Select allowClear options={[
                { value: 'pending_confirmation', label: 'Pending Confirmation' },
                { value: 'awaiting', label: 'Awaiting Payment' },
                { value: 'partial_paid', label: 'Partial Paid' },
                { value: 'paid', label: 'Paid' },
                { value: 'withdrawn', label: 'Withdrawn' },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} xl={3}>
            <Form.Item><Space><Button type="primary" htmlType="submit">Filter</Button><Button onClick={this.resetFilters}>Reset</Button></Space></Form.Item>
          </Col>
        </Row>
      </Form>
    );
  }

  bookingTable() {
    const { bookings, category } = this.state;
    const isGround = category === "ground";

    return (
      <Table
        dataSource={bookings}
        loading={this.state.loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          position: ["none", "bottomCenter"], current: this.state.currentPage, pageSize: LIMIT,
          total: this.state.total, showSizeChanger: false, itemRender,
          onChange: page => this.getBookings(page),
        }}
      >
        <Column title="Booking Date" dataIndex="bookingDate" key="bookingDate" />
        <Column title="Ref No." dataIndex="refNo" key="refNo" />
        <Column title="Name" dataIndex="name" key="name" />
        <Column title="Booked by" dataIndex="bookedBy" key="bookedBy" />
        <Column title="Tour Code" dataIndex="tourCode" key="tourCode" />
        <Column title="Pax" dataIndex="pax" key="pax" />
        <Column title="Destination" dataIndex="destination" key="destination" />
        <Column title="Package Name" dataIndex="packageName" key="packageName" />
        {isGround && <Column title="Departure Date" dataIndex="departureDate" key="departureDate" />}
        {!isGround && (
          <Column title="Itinerary" key="itinerary" render={(_: string, record: BookingSummary) => (
            <><a href={record.fileUrlCopy} download>EN</a>{" | "}<a href={record.fileCNUrlCopy} download>CN</a></>
          )} />
        )}
        {!isGround && bookings.some(booking => booking.bookingFormEnabled) && (
          <Column title="Booking Form" key="bookingForm" render={(_: string, record: BookingSummary) => {
            const disabled = !record.bookingFormUrl;
            return (
              <a href={record.bookingFormUrl} download aria-disabled={disabled}
                 onClick={event => disabled && event.preventDefault()}
                 style={{ pointerEvents: disabled ? 'none' : 'auto' }}>
                <Icon color={disabled ? '#ACACAC' : undefined} path={mdiDownload} size="25px" />
              </a>
            );
          }} />
        )}
        <Column title="Status" dataIndex="status" key="status" fixed="right" width={130}
          render={(status: string) => <Tag color={statusesColor[status]}>{status}</Tag>} />
        <Column title="Action" key="action" fixed="right" width={150}
          render={(_: unknown, record: BookingSummary) => (
            <Space size="middle">
              <Link to={isGround ? `/web/bookings/ground/${record.id}` : `/web/bookings/${record.id}`}>View</Link>
              {!isGround && record.payableText === 'Payable' && <Link to={`/web/bookings/${record.id}/payment`}>Pay Now</Link>}
            </Space>
          )} />
      </Table>
    );
  }

  render() {
    return (
      <div id="series-bookings" className="bookings-page b2c2b">
        <div className="bookings-page_header"><h1>My Bookings</h1></div>
        <div className="bookings-page_table b2c2b-section_panel">
          <Tabs activeKey={this.state.category} onChange={key => this.changeCategory(key as "series" | "ground")}
            items={[{ key: 'series', label: 'Series' }, { key: 'ground', label: 'Ground' }]} />
          {this.state.category === 'ground' && this.groundFilters()}
          <label>{this.state.category === 'ground' ? 'Ground Tour Bookings' : 'Series Bookings'}</label>
          {this.bookingTable()}
        </div>
      </div>
    );
  }
}

export default BookingHistoryPage;
